import { createDebugLog } from '@ydistri/utils';
import type {
  MultiselectCustomerPartialEntity,
  MultiselectKeysToParse,
  MultiselectParseResponse,
  ResolvedPastedData,
} from './configurationMultiselectTypes';

const debugLog = createDebugLog('ConfigurationMultiselect');

export const extractPastedValues = (
  content: string,
): { values: string[]; duplicateIds: string[] } => {
  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(content, 'text/html');
  const strippedContent = htmlDoc.body.textContent;
  const rows = htmlDoc.querySelectorAll('table tr');
  const foundValues = new Set<string>();
  const duplicateIds = new Set<string>();

  debugLog('rows', rows, 'length', rows.length);

  const addValue = (value: string) => {
    if (foundValues.has(value)) {
      duplicateIds.add(value);
    } else {
      foundValues.add(value);
    }
  };

  if (rows.length > 0) {
    rows.forEach(row => {
      row.querySelectorAll('td').forEach(cell => {
        let cellContent = cell.innerText;
        if (!cellContent) {
          cellContent = cell.firstChild?.textContent ?? '';
        }
        if (cellContent.length > 0) {
          addValue(cellContent.trim());
        }
      });
    });
  } else {
    strippedContent.split(/\s+|\n+|\r+|\t+|,|;/g).forEach(value => {
      const trimmedValue = value.trim();
      if (trimmedValue.length > 0) {
        addValue(trimmedValue);
      }
    });
  }

  return { values: [...foundValues], duplicateIds: [...duplicateIds] };
};

export const parseTabularDataFromHtml = (
  data: MultiselectCustomerPartialEntity[],
  content: string,
  compareAsInt: boolean = false,
  dataCheck?: MultiselectCustomerPartialEntity[],
): MultiselectParseResponse => {
  const response: MultiselectParseResponse = {
    ids: [],
    dataCheckOnlyIds: [],
    parsedBy: 'customerId',
    notRecognized: [],
    duplicateIds: [],
    counter: {
      customerId: 0,
      code: 0,
      total: 0,
    },
    availability: {
      customerId: true,
      code: false,
    },
  };

  const { values, duplicateIds } = extractPastedValues(content);
  const foundValuesString = new Set(values);
  response.duplicateIds = duplicateIds;
  response.counter.total = foundValuesString.size;

  const hasCode = [...data, ...(dataCheck ?? [])].some(entity => !!entity.code);
  response.availability.code = hasCode;

  const dataByCode: MultiselectCustomerPartialEntity[] = [];
  const dataByCustomerId: MultiselectCustomerPartialEntity[] = [];
  const dataCheckOnlyByCode: MultiselectCustomerPartialEntity[] = [];
  const dataCheckOnlyByCustomerId: MultiselectCustomerPartialEntity[] = [];

  const createLookup = (
    entities: MultiselectCustomerPartialEntity[],
    getKey: (entity: MultiselectCustomerPartialEntity) => string | undefined | null,
  ) => {
    return new Map(
      entities
        .map(entity => [getKey(entity), entity] as const)
        .filter(
          (entry): entry is readonly [string, MultiselectCustomerPartialEntity] => !!entry[0],
        ),
    );
  };
  const getCustomerIdKey = (value: string) => (compareAsInt ? parseInt(value).toString() : value);
  const dataByCustomerIdLookup = createLookup(data, entity =>
    compareAsInt ? entity.id.toString() : entity.customerId,
  );
  const dataCheckByCustomerIdLookup = createLookup(dataCheck ?? [], entity =>
    compareAsInt ? entity.id.toString() : entity.customerId,
  );
  const dataByCodeLookup = createLookup(data, entity => entity.code);
  const dataCheckByCodeLookup = createLookup(dataCheck ?? [], entity => entity.code);

  const notRecognizedCustomerId: string[] = [];
  const notRecognizedCode: string[] = [];
  foundValuesString.forEach(value => {
    const customerIdKey = getCustomerIdKey(value);
    const foundDataCheckElementCustomerId = dataCheckByCustomerIdLookup.get(customerIdKey);
    const foundElementCustomerId = dataByCustomerIdLookup.get(customerIdKey);
    if (dataCheck && !foundDataCheckElementCustomerId) {
      notRecognizedCustomerId.push(value);
    } else if (foundElementCustomerId) {
      dataByCustomerId.push(foundElementCustomerId);
    } else if (foundDataCheckElementCustomerId) {
      dataCheckOnlyByCustomerId.push(foundDataCheckElementCustomerId);
    } else {
      notRecognizedCustomerId.push(value);
    }

    if (hasCode) {
      const foundDataCheckElementCode = dataCheckByCodeLookup.get(value);
      const foundElementCode = dataByCodeLookup.get(value);
      if (dataCheck && !foundDataCheckElementCode) {
        notRecognizedCode.push(value);
      } else if (foundElementCode) {
        dataByCode.push(foundElementCode);
      } else if (foundDataCheckElementCode) {
        dataCheckOnlyByCode.push(foundDataCheckElementCode);
      } else {
        notRecognizedCode.push(value);
      }
    }
  });

  response.counter = {
    customerId: dataByCustomerId.length + dataCheckOnlyByCustomerId.length,
    code: dataByCode.length + dataCheckOnlyByCode.length,
    total: foundValuesString.size,
  };
  response.parsedBy = response.counter.customerId > response.counter.code ? 'customerId' : 'code';

  const filteredData = response.parsedBy === 'code' ? dataByCode : dataByCustomerId;
  const dataCheckOnly =
    response.parsedBy === 'code' ? dataCheckOnlyByCode : dataCheckOnlyByCustomerId;
  response.ids = filteredData.map(item => item.id);
  response.dataCheckOnlyIds = dataCheckOnly.map(item => item.id);
  response.notRecognized =
    response.parsedBy === 'code' ? notRecognizedCode : notRecognizedCustomerId;

  return response;
};

export const createResolvedPasteResponse = <T extends MultiselectCustomerPartialEntity>(
  values: string[],
  duplicateIds: string[],
  resolved: ResolvedPastedData<T>,
): MultiselectParseResponse => {
  const resolvedByCustomerId = resolved.data.filter(item =>
    values.includes(item.customerId),
  ).length;
  const resolvedByCode = resolved.data.filter(
    item => item.code !== null && item.code !== undefined && values.includes(item.code),
  ).length;
  const parsedBy: MultiselectKeysToParse =
    resolvedByCustomerId > resolvedByCode ? 'customerId' : 'code';

  return {
    ids: resolved.data.map(item => item.id),
    dataCheckOnlyIds: [],
    parsedBy,
    notRecognized: resolved.notRecognized,
    duplicateIds,
    counter: {
      customerId: resolvedByCustomerId,
      code: resolvedByCode,
      total: values.length,
    },
    availability: {
      customerId: true,
      code: resolved.data.some(item => !!item.code),
    },
  };
};
